import axios from "axios";
import swal from "sweetalert";
import { postErrorLog } from "./errorLog.service";
import { logout } from "./auth.service";
import { clearLocalStorageKeepGuestId } from "src/utils/toolProgressStorage";

export const axiosInstance = axios.create({
	baseURL: process.env.REACT_APP_API_URL || 'https://api-dev.brofesional.id'
	// baseURL: 'https://franken-api.brofesional.id'
});

axiosInstance.interceptors.request.use((config) => {
	let token;
	const requestUrl = config.url ?? "";
	const isReportPath = window.location.pathname.includes("/report/");
	const isTentorPath = window.location.pathname.includes("/tentor/");
	// Cek apakah ini adalah permintaan untuk logout
	const isLogoutRequest = requestUrl.includes("/api/auth/logout");

	if (isReportPath && isLogoutRequest) {
		// Khusus untuk logout di halaman report, gunakan "TOKEN"
		token = localStorage.getItem("TOKEN");
	} else if (isReportPath) {
		// Untuk semua request lain di halaman report, gunakan "PARENT_TOKEN"
		token = localStorage.getItem("PARENT_TOKEN");
	} else if (isTentorPath) {
		token = localStorage.getItem("TENTOR_TOKEN");
	} else {
		// Untuk halaman lainnya, gunakan "TOKEN" standar
		token = localStorage.getItem("TOKEN");
	}

	if (token) {
		config.headers["Authorization"] = "Bearer " + token;
	}

	return config;
});

axiosInstance.interceptors.response.use(
	(response) => {
		return response;
	},
	async function (error) {
		const isForceUpdateFlow = localStorage.getItem("FORCE_UPDATE_FLOW") === "1";
		const isMobileFlow = localStorage.getItem("isMobile") === "true";
		const isForceUpdateOrMobileFlow = isForceUpdateFlow || isMobileFlow;
		const isLogoutRequestError = error?.config?.url?.includes("/api/auth/logout");
		const isBugLoggerRequest = error?.config?.url?.includes("/api/feature/bug-logger/");
		// Form unlock tools menampilkan error 422/429/5xx sendiri.
		const isToolLeadRequest = error?.config?.url?.includes("/api/feature/tool-lead");

		if (isBugLoggerRequest || isToolLeadRequest) {
			return Promise.reject(error);
		}

		if (
			error.response?.status === 401 &&
			window.location.pathname !== "/login"
		) {
			swal({
				title: "Unauthorized",
				text: "Sesi berakhir, silahkan login kembali",
				icon: "error",
			}).then(() => {
				localStorage.removeItem("TOKEN");
				localStorage.removeItem("USER");
				window.location.href = "/login";
				postErrorLog(error);
			});
		} else if (error.response?.status === 404) {
			swal({
				title: "Not Found",
				text: error.response?.data?.message ?? "Halaman tidak ditemukan",
				icon: "error",
			}).then(() => {
				postErrorLog(error);
			});
		} else if (error.response?.status === 500) {
			swal({
				title: "Internal Server Error",
				text: "Terjadi kesalahan pada server",
				icon: "error",
			}).then(() => {
				postErrorLog(error);
			});
		} else if (error.response?.status === 403) {
			if (isForceUpdateOrMobileFlow) {
				try {
					await postErrorLog(error);
				} catch (logError) {
					console.error("Failed to send bug logger on 403 force/mobile flow:", logError);
				}

				// Cegah loading logout nyangkut akibat loop request /api/auth/logout yang terus 403.
				// Pada force update/mobile flow cukup cleanup client-side lalu redirect login.
				const submateriProgress = JSON.parse(
					localStorage.getItem("SubmateriProgress") || "[]"
				);
				const seeTourDashboardCounter = localStorage.getItem("seeTourDashboardCounter");
				const forceUpdateFlowValue = localStorage.getItem("FORCE_UPDATE_FLOW");
				const isMobileValue = localStorage.getItem("isMobile");

				clearLocalStorageKeepGuestId();
				localStorage.setItem("SubmateriProgress", JSON.stringify(submateriProgress));
				if (seeTourDashboardCounter) {
					localStorage.setItem("seeTourDashboardCounter", seeTourDashboardCounter);
				}
				if (isMobileValue) {
					localStorage.setItem("isMobile", isMobileValue);
				}
				if (forceUpdateFlowValue === "1") {
					localStorage.setItem("FORCE_UPDATE_FLOW", "1");
				}

				// Pastikan overlay logout dimatikan sebelum pindah halaman.
				try {
					window.dispatchEvent(new CustomEvent("logout-loading", { detail: false }));
				} catch (dispatchError) {
					console.error("Failed to reset logout loading state:", dispatchError);
				}

				if (window.location.pathname !== "/login") {
					window.location.href = "/login";
				}

				if (isLogoutRequestError) {
					return Promise.reject(error);
				}
				return Promise.reject(error);
			}

			swal({
				title: "Forbidden",
				text: "Anda tidak memiliki akses ke halaman ini",
				icon: "error",
			}).then(async () => {
				try {
					await postErrorLog(error);
				} catch (logError) {
					console.error("Failed to send bug logger on 403:", logError);
				}
				await logout(true)
			});
		} else if (error.response?.status === 405) {
			swal({
				title: "Method Not Allowed",
				text: "Method tidak diizinkan",
				icon: "error",
			}).then(() => {
				postErrorLog(error);
			});
		} else if (error.response?.status === 409 && error.response?.data?.message !== "Device limit reached") {
			swal({
				title: "Conflict",
				text: "Data sudah ada",
				icon: "error",
			}).then(() => {
				postErrorLog(error);
			});
		} else if (error.response?.status === 422) {
			// mini-progress 422 = business rule (butuh ≥2 tries), UI handle sendiri
			const isMiniProgress = error?.config?.url?.includes("/mini-progress/");
			if (!isMiniProgress) {
				swal({
					title: "Oops!",
					text: error.response.data.message,
					icon: "error",
				});
			}
		}
		return Promise.reject(error);
	}
);
